import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  GEOPL_API_PROXY_ENDPOINT,
  GEO_CODING_REVERSE_API_ENDPOINT,
  createGeoplFetcher,
} from '@e-socna/gis';
import { isDefined } from '@e-socna/utils';
import type {
  Address,
  GeoCodingResult,
  LatLonCoordinates,
  Parcel,
} from '@e-socna/gis';

import { environment } from '../../../../environment';

interface Params<I extends 'parcel' | 'address'> {
  readonly position: LatLonCoordinates | undefined;
  readonly index: I;
  readonly limit?: number;
  readonly returntruegeometry?: boolean;
  readonly enabled?: boolean;
}

const geoplFetch = createGeoplFetcher('geocoding');

export const useGeoCodingReverseQuery = <I extends 'parcel' | 'address'>(
  pParams: Params<I>,
) => {
  const { fetcher, lon, lat, enabled } = useMemo(() => {
    const lon = pParams.position?.lon.toFixed(6);
    const lat = pParams.position?.lat.toFixed(6);

    const enabled = !!(
      (isDefined(pParams.enabled) ? pParams.enabled : true) &&
      lat &&
      lon
    );

    const params = {
      ...(lon && { lon }),
      ...(lat && { lat }),
      index: pParams.index,
      limit: pParams.limit?.toFixed() ?? '1',
      returntruegeometry: pParams.returntruegeometry
        ? String(pParams.returntruegeometry)
        : 'false',
    };

    const queryString = new URLSearchParams(params).toString();

    const url = `${environment.backendUrl}${GEOPL_API_PROXY_ENDPOINT}/${GEO_CODING_REVERSE_API_ENDPOINT}?${queryString}`;

    return {
      fetcher: () =>
        geoplFetch<GeoCodingResult<I extends 'address' ? Address : Parcel>>(
          url,
        ),
      lon,
      lat,
      enabled,
    };
  }, [
    pParams.enabled,
    pParams.index,
    pParams.limit,
    pParams.position?.lat,
    pParams.position?.lon,
    pParams.returntruegeometry,
  ]);

  return useQuery({
    queryKey: [
      'useGeoCodingReverseQuery',
      lon,
      lat,
      pParams.index,
      pParams.limit,
      pParams.returntruegeometry,
    ],
    queryFn: fetcher,
    enabled,
  });
};
